import https from './https'
import router from '@/router'
import store from '@/store'

const tongxin = {
  /**
   * 取得體檢/健檢分類樹狀 Menu
   * @param {string} clinicId - 診所 ID
   * @param {string|null} type - 類型 (self-pay/public-fund/all)
   * @param {string|null} categoryId - 分類 ID（獲取該分類下的子分類）
   */
  getCategories(clinicId, type = null, categoryId = null) {
    const orgCode = router.currentRoute.params.org_code
    const params = new URLSearchParams()

    params.append('clinicId', clinicId)

    if (type) {
      params.append('type', type)
    }

    if (categoryId) {
      params.append('categoryId', categoryId)
    }

    const sourceType = store.getters['web/tongxin/sourceType']
    if (sourceType) {
      params.append('sourceType', sourceType)
    }

    const queryString = params.toString()
    return https.get(`${orgCode}/tongxin/categories${queryString ? '?' + queryString : ''}`).then(r => r.data)
  },

  /**
   * 取得健檢項目詳細內容與加值選項
   * @param {string} subcategoryId - 子分類 ID
   * @param {string} clinicId - 診所 ID
   */
  getSubcategoryDetail(subcategoryId, clinicId) {
    const orgCode = router.currentRoute.params.org_code
    return https.get(`${orgCode}/tongxin/subcategories/${subcategoryId}?clinicId=${clinicId}`).then(r => r.data)
  },

  /**
   * 取得健檢可預約日期與時段
   * @param {string} rowId - 識別碼
   * @param {string} clinicId - 診所 ID
   * @param {string|null} dateFrom - 查詢起始日期
   * @param {string|null} dateTo - 查詢結束日期
   */
  getAvailableDates(rowId, clinicId, dateFrom = null, dateTo = null) {
    const orgCode = router.currentRoute.params.org_code
    const payload = {
      rowId: rowId,
      clinicId: clinicId
    }

    if (dateFrom) {
      payload.dateFrom = dateFrom
    }

    if (dateTo) {
      payload.dateTo = dateTo
    }

    return https.post(`${orgCode}/tongxin/available-dates`, payload).then(r => r.data)
  },

  /**
   * 取得 Header 圖片
   * @param {string} clinicId - 診所 ID
   */
  getHeaderPhoto(clinicId) {
    const orgCode = router.currentRoute.params.org_code
    return https.get(`${orgCode}/tongxin/header-photo?clinicId=${clinicId}`).then(r => r.data)
  },

  /**
   * 透過 clinicCode 查詢診所資訊
   * @param {string} clinicCode - 診所代碼（必填）
   */
  getClinicByCode(clinicCode) {
    const orgCode = router.currentRoute.params.org_code
    return https.get(`${orgCode}/tongxin/clinics/${clinicCode}`).then(r => r.data)
  },

  /**
   * 建立健檢預約
   * @param {Object} payload - 預約資料
   * @param {string} payload.clinicId - 診所 ID（必填）
   * @param {string} payload.rowId - 識別碼（必填）
   * @param {string} payload.visitDate - 看診日期，格式 YYYY-MM-DD（必填）
   * @param {number} payload.slotValue - 時段代碼 1=上午診, 2=午診, 3=晚診（必填）
   * @param {Object} payload.consent - 同意條款資訊（必填）
   * @param {string} payload.consent.tosVer - 服務條款版本（必填）
   * @param {string} payload.consent.privacyVer - 隱私政策版本（必填）
   * @param {string} payload.consent.acceptedAt - 同意時間（必填）
   * @param {string} [payload.categoryId] - 所屬分類（選填）
   * @param {string} [payload.subcategoryId] - 所屬次分類（選填）
   * @param {string} [payload.packageId] - 套餐 ID（選填）
   * @param {string[]} [payload.optionIds] - 加值選項 ID 列表（選填）
   * @param {string} [payload.source] - 來源，預設 WEB（選填）
   * @param {string} [payload.remark] - 備註（選填）
   * @param {Object} [payload.patient] - 病患資料（前端有帶則優先使用）
   * @param {string} [payload.patient.name] - 姓名
   * @param {string} [payload.patient.ssid] - 身分證字號
   * @param {string} [payload.patient.birthday] - 出生日期，格式 YYYY-MM-DD
   * @param {Object} [payload.contact] - 聯絡資訊（有值才需傳）
   * @param {string} [payload.contact.mobile] - 手機號碼
   * @param {string} [payload.contact.email] - Email
   */
  createReservation(payload) {
    const orgCode = router.currentRoute.params.org_code
    return https.post(`${orgCode}/tongxin/reservations`, payload).then(r => r.data)
  },

  /**
   * 取得預約列表
   * @param {string} ssid - 身分證字號（必填）
   * @param {string} birthday - 出生日期，格式 YYYY-MM-DD（必填）
   * @param {string|null} status - 篩選狀態 (all/confirmed/cancelled)
   * @param {string|null} from - 查詢起始日期
   * @param {string|null} to - 查詢結束日期
   * @param {number} limit - 回傳筆數限制
   * @param {number} offset - 分頁起始點
   */
  getReservations(ssid, birthday, status = 'all', from = null, to = null, limit = 20, offset = 0) {
    const orgCode = router.currentRoute.params.org_code
    const clinicId = store.getters['web/tongxin/clinicId']
    const params = new URLSearchParams()

    params.append('clinicId', clinicId)
    params.append('ssid', ssid)
    params.append('birthday', birthday)
    params.append('status', status)
    params.append('limit', limit)
    params.append('offset', offset)

    if (from) {
      params.append('from', from)
    }

    if (to) {
      params.append('to', to)
    }

    return https.get(`${orgCode}/tongxin/reservations?${params.toString()}`).then(r => r.data)
  },

  /**
   * 取消預約 (API 2.6)
   * @param {string} reservationId - 預約識別碼
   * @param {string|null} remark - 取消原因
   */
  cancelReservation(reservationId, remark = null) {
    const orgCode = router.currentRoute.params.org_code
    const payload = { reservationId }
    if (remark) {
      payload.remark = remark
    }
    return https.put(`${orgCode}/tongxin/reservations`, payload).then(r => r.data)
  },

  /**
   * 查詢親友列表 (SAW 2.1)
   * Backend 從 Auth::user() 取得 customer 資料
   */
  getRelations() {
    const orgCode = router.currentRoute.params.org_code
    return https.get(`${orgCode}/tongxin/family/relations`).then(r => r.data)
  },

  /**
   * 查詢本人資料 (SAW 1.2)
   */
  getCustomerInfo() {
    const orgCode = router.currentRoute.params.org_code
    return https.get(`${orgCode}/tongxin/customer`).then(r => r.data)
  },

  /**
   * 新增親友 (SAW 2.2)
   * @param {Object} payload - 親友資料
   * @param {string} payload.ptName - 親友姓名
   * @param {string} payload.contactPTIdno - 親友身分證字號
   * @param {string} payload.ptBirthday - 親友出生年月日 (YYYY-MM-DD)
   * @param {string} payload.patientRelationship - 與本人關係
   * @param {string} payload.createSource - 來源 (選填，預設 WEB)
   * Backend 從 Auth::user() 自動帶入 principalPTIdno
   */
  createRelation(payload) {
    const orgCode = router.currentRoute.params.org_code
    return https.post(`${orgCode}/tongxin/family/relations`, payload).then(r => r.data)
  },

  /**
   * 更新親友資料 (SAW 2.3)
   * @param {Object} payload - 親友資料
   * @param {string} payload.rowid - 記錄 ID
   * @param {string} payload.ptName - 親友姓名
   * @param {string} payload.contactPTIdno - 親友身分證字號
   * @param {string} payload.ptBirthday - 親友出生年月日 (YYYY-MM-DD)
   * @param {string} payload.patientRelationship - 與本人關係
   * @param {string} payload.createSource - 來源 (選填，預設 WEB)
   * Backend 從 Auth::user() 自動帶入 principalPTIdno，並檢查授權狀態
   */
  updateRelation(payload) {
    const orgCode = router.currentRoute.params.org_code
    return https.put(`${orgCode}/tongxin/family/relations`, payload).then(r => r.data)
  },

  /**
   * 刪除親友 (SAW 2.4)
   * @param {Object} payload - 刪除參數
   * @param {string} payload.rowid - 記錄 ID
   * @param {string} payload.contactPTIdno - 親友身分證字號
   * Backend 從 Auth::user() 自動帶入 principalPTIdno，並檢查授權狀態
   */
  deleteRelation(payload) {
    const orgCode = router.currentRoute.params.org_code
    return https.delete(`${orgCode}/tongxin/family/relations`, { data: payload }).then(r => r.data)
  },

  /**
   * 解除綁定
   */
  unbind() {
    const orgCode = router.currentRoute.params.org_code
    return https.post(`${orgCode}/tongxin/unbind`).then(r => r.data)
  }
}

export default tongxin
